import { getAPIBaseURL } from 'app/api/utils';
import { getAPIGroupVersions } from 'app/features/apiserver/discovery';

const QUERIES_API_GROUP = 'queries.grafana.app' as const;

type QueriesAPIVersion = 'v1' | 'v1beta1';

const FALLBACK_VERSION: QueriesAPIVersion = 'v1beta1';

/**
 * Negotiates the served API version instead of discovering it from a failed
 * request. Authorization runs before routing in the apiserver, and authorizers
 * are registered per group *and* version — so calling a version the backend
 * does not serve is rejected by the org-role fallback authorizer with a 403,
 * not the 404 a "try v1, fall back on failure" client would need to react to.
 */
class QueriesAPIVersionResolver {
  private resolved: QueriesAPIVersion | null = null;
  private resolving: Promise<QueriesAPIVersion> | null = null;

  /**
   * Resolves the queries API version from GET /apis/queries.grafana.app/ (cached).
   * On failure, returns v1beta1 without caching so the next call retries discovery.
   */
  async resolve(): Promise<QueriesAPIVersion> {
    if (this.resolved) {
      return this.resolved;
    }

    if (!this.resolving) {
      this.resolving = this.discover()
        .then((version) => {
          this.resolved = version;
          return version;
        })
        .catch(() => FALLBACK_VERSION)
        .finally(() => {
          this.resolving = null;
        });
    }

    return this.resolving;
  }

  private async discover(): Promise<QueriesAPIVersion> {
    // Returns undefined when the group is not registered at all (404).
    const group = await getAPIGroupVersions(QUERIES_API_GROUP);
    if (!group) {
      return FALLBACK_VERSION;
    }

    const preferred = group.preferred;
    if (preferred === 'v1' || preferred === 'v1beta1') {
      return preferred;
    }
    if (group.versions.has('v1')) {
      return 'v1';
    }
    return FALLBACK_VERSION;
  }

  /** @internal Testing only */
  reset(): void {
    this.resolved = null;
    this.resolving = null;
  }

  /** @internal Testing only */
  set(version: QueriesAPIVersion): void {
    this.resolved = version;
  }
}

export const queriesAPIVersionResolver = new QueriesAPIVersionResolver();

export async function getQueriesAPIBaseURL(): Promise<string> {
  const version = await queriesAPIVersionResolver.resolve();
  return getAPIBaseURL(QUERIES_API_GROUP, version);
}
